import React, { useState } from "react";
import { handleTokenAndPostRequest } from "../utils/utils";
import { trackAgentEvent } from "../utils/agentEventTracking";
import {
  FaTimes, FaPlug, FaCheckCircle, FaExclamationTriangle,
  FaKey, FaUser, FaLock, FaGlobe, FaArrowLeft,
} from "react-icons/fa";

const SIDEBAR_WIDTH = 260;

const CRM_OPTIONS = [
  { value: "agentbox", label: "AgentBox", type: "login" },
  { value: "eagle", label: "Eagle", type: "eagle" },
  { value: "lockedon", label: "LockedOn", type: "login" },
  { value: "vaultre", label: "VaultRE", type: "login" },
  { value: "boxdice", label: "Box+Dice", type: "login" },
  { value: "rex", label: "Rex", type: "login" },
  { value: "pipedrive", label: "Pipedrive", type: "apikey" },
  { value: "other", label: "Other", type: "other" },
];

export default function ConnectCRMModal({ show, onClose, onSuccess, fullScreen }) {
  const [step, setStep] = useState("select"); // select | form
  const [selectedCrm, setSelectedCrm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);

  // Form fields
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loginUrl, setLoginUrl] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [otherName, setOtherName] = useState("");
  const [label, setLabel] = useState("");
  const [agentboxScope, setAgentboxScope] = useState("primary");

  React.useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  if (!show) return null;

  const resetForm = () => {
    setStep("select");
    setSelectedCrm(null);
    setClientId("");
    setClientSecret("");
    setUsername("");
    setPassword("");
    setLoginUrl("");
    setApiKey("");
    setOtherName("");
    setLabel("");
    setAgentboxScope("primary");
    setResult(null);
    setSaving(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSelectCrm = (crm) => {
    setSelectedCrm(crm);
    setLabel(crm.label);
    setResult(null);
    setStep("form");
    trackAgentEvent({
      eventName: `integrations.crm.${crm.value}.click`,
      metadata: { crm_type: crm.value, crm_label: crm.label },
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setResult(null);

    const body = { crm_type: selectedCrm.value, label };

    if (selectedCrm.type === "eagle") {
      if (!clientId.trim() || !clientSecret.trim()) {
        setResult({ success: false, message: "Client ID and Client Secret are required." });
        setSaving(false);
        return;
      }
      body.client_id = clientId.trim();
      body.client_secret = clientSecret.trim();
    } else if (selectedCrm.type === "apikey") {
      if (!apiKey.trim()) {
        setResult({ success: false, message: "API key is required." });
        setSaving(false);
        return;
      }
      body.api_key = apiKey.trim();
    } else if (selectedCrm.type === "login") {
      if (!username.trim() || !password.trim() || !loginUrl.trim()) {
        setResult({ success: false, message: "Username, password, and login URL are required." });
        setSaving(false);
        return;
      }
      body.username = username.trim();
      body.password = password.trim();
      body.login_url = loginUrl.trim();
      if (selectedCrm.value === "agentbox") {
        body.agentbox_scope = agentboxScope;
      }
    } else if (selectedCrm.type === "other") {
      if (!otherName.trim()) {
        setResult({ success: false, message: "Please enter your CRM name." });
        setSaving(false);
        return;
      }
      body.other_crm_name = otherName.trim();
    }

    try {
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/save-crm-integration`,
        body,
        { "Content-Type": "application/json" }
      );
      const successMsg = selectedCrm.type === "other"
        ? res.data?.message || "We've received your request. Our team will be in touch."
        : "Your CRM has been connected. Your contacts are now being loaded and addresses matched to the VendorLeads database — this typically takes 24 hours.";
      setResult({ success: true, message: successMsg });
      if (selectedCrm.type !== "other") {
        window.dispatchEvent(new Event("crm-connected"));
      }
      if (onSuccess) onSuccess();
    } catch (err) {
      const msg = err?.response?.data?.message || "Failed to save integration. Please try again.";
      setResult({ success: false, message: msg });
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "10px 12px", borderRadius: 8,
    border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box",
    fontFamily: "inherit",
  };

  const renderSelectStep = () => (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <FaPlug size={20} color="#0f766e" />
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>
          Connect Your CRM
        </h2>
      </div>
      <p style={{ fontSize: 14, color: "#4b5563", lineHeight: 1.6, margin: "8px 0 20px" }}>
        Select your CRM below to connect it. Our AI agent will securely access your contacts — browsing your CRM like a human would — to start working for you.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {CRM_OPTIONS.map((crm) => (
          <button
            key={crm.value}
            onClick={() => handleSelectCrm(crm)}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "14px 16px", borderRadius: 10,
              border: "1px solid #e5e7eb", background: "#fff",
              cursor: "pointer", textAlign: "left",
              transition: "border-color 0.15s, box-shadow 0.15s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#0f766e";
              e.currentTarget.style.boxShadow = "0 0 0 2px rgba(15,118,110,0.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#e5e7eb";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              background: "linear-gradient(135deg, #0f766e 0%, #10b981 100%)",
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <FaPlug size={14} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{crm.label}</div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {crm.type === "eagle" ? "Connect with Client ID & Secret"
                  : crm.type === "apikey" ? "Connect with your API key"
                  : crm.type === "login" ? "Connect with login credentials"
                  : "Tell us your CRM"}
              </div>
            </div>
          </button>
        ))}
      </div>
    </>
  );

  const renderFormStep = () => {
    if (!selectedCrm) return null;

    return (
      <>
        <button
          onClick={() => { setStep("select"); setResult(null); }}
          style={{
            background: "none", border: "none", cursor: "pointer",
            display: "flex", alignItems: "center", gap: 6, padding: 0,
            fontSize: 13, color: "#6b7280", marginBottom: 12,
          }}
        >
          <FaArrowLeft size={11} /> Back
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: "linear-gradient(135deg, #0f766e 0%, #10b981 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <FaPlug size={12} color="#fff" />
          </div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#111827" }}>
            Connect {selectedCrm.label}
          </h2>
        </div>

        {selectedCrm.type === "eagle" && (
          <>
            <p style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.6, margin: "8px 0 16px" }}>
              Enter your Eagle Client ID and Client Secret below. Your credentials are encrypted and stored securely — we never store them in plain text.
            </p>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                <FaKey size={11} color="#6b7280" /> Client ID
              </label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder="Your Eagle Client ID"
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                <FaLock size={11} color="#6b7280" /> Client Secret
              </label>
              <input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="Your Eagle Client Secret"
                style={inputStyle}
              />
            </div>
          </>
        )}

        {selectedCrm.type === "apikey" && (
          <>
            <p style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.6, margin: "8px 0 16px" }}>
              Enter your {selectedCrm.label} API key below. In {selectedCrm.label}, you'll find it under
              {" "}Settings → Personal preferences → API. Your key is encrypted and stored securely — we never store it in plain text.
            </p>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                <FaKey size={11} color="#6b7280" /> API Key
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={`Your ${selectedCrm.label} API key`}
                style={inputStyle}
              />
            </div>
          </>
        )}

        {selectedCrm.type === "login" && (
          <>
            <div style={{
              background: "#f0fdfa", border: "1px solid #ccfbf1", borderRadius: 10,
              padding: "12px 14px", margin: "8px 0 16px", fontSize: 13, color: "#134e4a", lineHeight: 1.6,
            }}>
              <strong>How it works:</strong> Our AI agent uses a secure browser — just like a human — to log in to your {selectedCrm.label} account and view your contact information. Your credentials are encrypted and stored securely.
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                <FaUser size={11} color="#6b7280" /> Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Your login username or email"
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                <FaLock size={11} color="#6b7280" /> Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your login password"
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
                <FaGlobe size={11} color="#6b7280" /> Login URL
              </label>
              <input
                type="url"
                value={loginUrl}
                onChange={(e) => setLoginUrl(e.target.value)}
                placeholder="Your CRM login URL"
                style={inputStyle}
              />
            </div>
            {selectedCrm.value === "agentbox" && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 6, display: "block" }}>
                  Contacts to sync
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#374151", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="agentbox_scope"
                      value="primary"
                      checked={agentboxScope === "primary"}
                      onChange={() => setAgentboxScope("primary")}
                      style={{ marginTop: 3 }}
                    />
                    <span><strong>My contacts only</strong> — only contacts where I'm the Primary Owner.</span>
                  </label>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#374151", cursor: "pointer" }}>
                    <input
                      type="radio"
                      name="agentbox_scope"
                      value="all"
                      checked={agentboxScope === "all"}
                      onChange={() => setAgentboxScope("all")}
                      style={{ marginTop: 3 }}
                    />
                    <span><strong>All contacts</strong> — every contact in the AgentBox account.</span>
                  </label>
                </div>
              </div>
            )}
          </>
        )}

        {selectedCrm.type === "other" && (
          <>
            <p style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.6, margin: "8px 0 16px" }}>
              Tell us which CRM you use and we'll reach out to get you connected.
            </p>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 4, display: "block" }}>
                CRM Name
              </label>
              <input
                type="text"
                value={otherName}
                onChange={(e) => setOtherName(e.target.value)}
                placeholder="e.g. PropertyMe, Rex, Console Cloud…"
                style={inputStyle}
              />
            </div>
          </>
        )}

        {/* Result message */}
        {result && (
          <div style={{
            display: "flex", alignItems: "flex-start", gap: 8,
            padding: "10px 14px", borderRadius: 10, marginBottom: 14,
            background: result.success ? "#f0fdf4" : "#fef2f2",
            border: result.success ? "1px solid #bbf7d0" : "1px solid #fecaca",
            fontSize: 13, color: result.success ? "#166534" : "#991b1b", lineHeight: 1.5,
          }}>
            {result.success
              ? <FaCheckCircle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
              : <FaExclamationTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
            }
            <span>{result.message}</span>
          </div>
        )}

        {/* Save button */}
        {!result?.success && (
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
              background: saving ? "#d1d5db" : "linear-gradient(135deg, #0f766e 0%, #10b981 100%)",
              color: "#fff", fontSize: 15, fontWeight: 600,
              cursor: saving ? "default" : "pointer",
              transition: "opacity 0.2s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            }}
          >
            {saving && (
              <span style={{
                display: "inline-block", width: 14, height: 14,
                border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff",
                borderRadius: "50%", animation: "crmSpin 0.7s linear infinite",
              }} />
            )}
            {saving ? "Saving..." : selectedCrm.type === "other" ? "Submit Request" : "Save Integration"}
          </button>
        )}

        {result?.success && (
          <button
            onClick={handleClose}
            style={{
              width: "100%", padding: "12px 0", borderRadius: 10, border: "none",
              background: "linear-gradient(135deg, #0f766e 0%, #10b981 100%)",
              color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer",
            }}
          >
            Done
          </button>
        )}
      </>
    );
  };

  return (
    <div
      onClick={handleClose}
      style={{
        position: "fixed", top: 0, left: isDesktop && !fullScreen ? SIDEBAR_WIDTH : 0, right: 0, bottom: 0,
        background: "rgba(0,0,0,0.45)", zIndex: 2000,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      <style>{`@keyframes crmSpin { to { transform: rotate(360deg); } }`}</style>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 16, width: "95%", maxWidth: 520,
          boxShadow: "0 8px 40px rgba(0,0,0,0.18)", padding: "32px 28px 28px",
          position: "relative", maxHeight: "90vh", overflowY: "auto",
        }}
      >
        <button
          onClick={handleClose}
          style={{
            position: "absolute", top: 16, right: 16, background: "none",
            border: "none", cursor: "pointer", padding: 4,
          }}
        >
          <FaTimes size={18} color="#9ca3af" />
        </button>

        {step === "select" ? renderSelectStep() : renderFormStep()}
      </div>
    </div>
  );
}
